/**
 * Success Gauge Calculator for SAFECARE Simulator
 * Computes 0-10 composite score based on 6 dimensions
 */

/**
 * Calculate health spending sub-score (0-10)
 * Compares SAFECARE vs baseline health spending as % of GDP in year 10
 */
/**
 * Calculate health spending sub-score (0-10)
 * Compares SAFECARE vs baseline health spending as % of GDP in year 10
 * Bonus for positive GDP impact
 */
function calculateHealthSpendingScore(yearlyResults, macroImpacts) {
    const year10 = yearlyResults[yearlyResults.length - 1];
    const baselineShare = year10.baselineHealthShareGDP * 100; // Convert to %
    const safecareShare = year10.safecareHealthShareGDP * 100;

    let improvement = baselineShare - safecareShare; // Percentage points saved

    // Macro contribution (GDP growth helps reduce finding burdens)
    if (macroImpacts && macroImpacts.length > 0) {
        const lastMacro = macroImpacts[macroImpacts.length - 1];
        if (lastMacro.gdpImpact > 0) {
            improvement += lastMacro.gdpImpact * 0.5; // Bonus for GDP growth
        }
    }

    // 10 points: 12+ pp improvement (27% → 15%)
    // 5 points: 6 pp improvement (27% → 21%)
    // 0 points: 0 pp improvement
    return Math.max(0, Math.min(10, improvement * (10 / 12)));
}

/**
 * Calculate federal debt sub-score (0-10)
 * Compares SAFECARE vs baseline debt-to-GDP in year 10
 * Bonus for positive GDP impact
 */
function calculateFederalDebtScore(yearlyResults, macroImpacts) {
    const year10 = yearlyResults[yearlyResults.length - 1];
    const baselineDebt = year10.baselineDebtToGDP * 100;
    const safecareDebt = year10.safecareDebtToGDP * 100;

    let improvement = baselineDebt - safecareDebt; // Percentage points reduced

    // Macro contribution (GDP growth reduces debt-to-GDP denominator)
    if (macroImpacts && macroImpacts.length > 0) {
        const lastMacro = macroImpacts[macroImpacts.length - 1];
        if (lastMacro.gdpImpact > 0) {
            improvement += lastMacro.gdpImpact * 1.5; // Stronger bonus for debt metric
        }
    }

    // 10 points: 50 pp reduction (adjusted for realistic range)
    // 5 points: 25 pp reduction
    // 0 points: 0 pp reduction or worse
    // FIX: Scale denominator increased from 20 to 50 to reflect actual impact range
    return Math.max(0, Math.min(10, improvement * (10 / 50)));
}

// ... [calculateHouseholdBurdenScore and calculateCoverageScore and calculateProviderStabilityScore remain unchanged] ...

/**
 * Calculate household burden sub-score (0-10)
 * Based on average savings for bottom 50% + progressivity bonus
 */
function calculateHouseholdBurdenScore(householdBurden) {
    // Bottom 5 deciles (bottom 50%)
    const bottom50 = householdBurden.slice(0, 5);
    const avgSavingsBottom50 = bottom50.reduce((sum, d) => sum + d.savings, 0) / 5;

    // Top 5 deciles
    const top50 = householdBurden.slice(5, 10);
    const avgSavingsTop50 = top50.reduce((sum, d) => sum + d.savings, 0) / 5;

    // Base score: savings amount
    // 10 points: $3,000+ savings for bottom 50%
    // 5 points: $1,500 savings
    // 0 points: <$500 savings
    let score = Math.max(0, Math.min(10, avgSavingsBottom50 / 300));

    // Progressivity bonus: +2 if bottom 50% saves more % of income than top 50%
    const avgPercentBottom = bottom50.reduce((sum, d) => sum + d.savingsPercent, 0) / 5;
    const avgPercentTop = top50.reduce((sum, d) => sum + d.savingsPercent, 0) / 5;

    if (avgPercentBottom > avgPercentTop) {
        score = Math.min(10, score + 2);
    }

    return score;
}

/**
 * Calculate coverage sub-score (0-10)
 * Based on uninsured rate and medical debt reduction
 * V2: Accounts for state cooperation and start year delay
 */
function calculateCoverageScore(yearlyResults, config) {
    const year10 = yearlyResults[yearlyResults.length - 1];

    // Estimate uninsured rate based on state cooperation and enforcement
    // Baseline: 8-10% uninsured
    // SAFECARE best case: 2% uninsured
    const baselineUninsured = 0.09;
    const stateCooperation = config.stateCooperation || 0.7;
    const enforcementAvg = (
        config.enforceDrugCeilings +
        config.enforceAdminSimplification +
        config.enforceTuitionCaps
    ) / 3;

    // V2: State cooperation has major impact on coverage
    const safecareUninsured = 0.02 + (1 - stateCooperation) * 0.04 + (1 - enforcementAvg) * 0.02;

    // V2: Start year delay penalty
    const yearsDelayed = config.startYear - 2027;
    const delayPenalty = yearsDelayed * 0.005; // 0.5% more uninsured per year delayed

    const adjustedUninsured = safecareUninsured + delayPenalty;

    // Medical debt reduction
    const debtReduction = (year10.medicalDebtBaseline - year10.medicalDebtSafecare) / year10.medicalDebtBaseline;

    // Score components
    const uninsuredScore = Math.max(0, (baselineUninsured - adjustedUninsured) / 0.07 * 5); // 0-5 points
    const debtScore = debtReduction * 5; // 0-5 points

    return uninsuredScore + debtScore;
}

/**
 * Calculate provider stability sub-score (0-10)
 * Based on payment rates and rural access
 * V2: Uses actual payment stringency parameter
 */
function calculateProviderStabilityScore(config) {
    const paymentRate = config.providerPaymentStringency || 1.0;
    const ruralUplift = config.ruralSafetyNetUplift || 0.05;

    // Estimate % of hospitals in financial distress
    // Baseline: 15-20% in distress
    // Payment at 100%: 10% in distress
    // Payment at 90%: 30% in distress
    // Payment at 110%: 5% in distress

    const baselineDistress = 0.18;
    let safecareDistress = 0.10 + (1.0 - paymentRate) * 2.0; // Highly sensitive to payment rates

    // Rural uplift reduces distress significantly
    safecareDistress *= (1.0 - ruralUplift * 2.0);
    safecareDistress = Math.max(0.02, Math.min(0.40, safecareDistress)); // Cap between 2-40%

    const improvement = baselineDistress - safecareDistress;

    // 10 points: <5% in distress (13+ pp improvement)
    // 5 points: <12% in distress (6 pp improvement)
    // 0 points: >20% in distress (no improvement)
    return Math.max(0, Math.min(10, improvement * (10 / 0.13)));
}

/**
 * Calculate workforce sub-score (0-10)
 * Based on med school debt reduction, graduate numbers, and primary care share
 * V2: Accounts for tuition support coverage and service obligations + Unemployment
 */
function calculateWorkforceScore(yearlyResults, workforceImpact, config, macroImpacts) {
    const year10 = yearlyResults[yearlyResults.length - 1];

    // Med school debt reduction
    const debtReduction = (year10.medSchoolDebtBaseline - year10.medSchoolDebtSafecare) /
        year10.medSchoolDebtBaseline;
    const debtScore = debtReduction * 3; // 0-3 points (reduced weight for new metric)

    // Graduate capacity (affected by tuition support)
    const tuitionSupport = config.tuitionSupportCoverage || 0.8;
    const capacityMultiplier = 0.8 + tuitionSupport * 0.4; // 80-120% of baseline
    const capacityScore = Math.min(2.5, capacityMultiplier * 2.5); // 0-2.5 points

    // Primary care share (affected by debt relief + service obligations)
    const serviceObligation = config.serviceObligationStrength || 0.5;
    const primaryCareBoost = debtReduction * 0.10 + serviceObligation * 0.05;
    const primaryCareShare = 0.30 + primaryCareBoost;
    const primaryCareScore = Math.max(0, (primaryCareShare - 0.30) / 0.10 * 2.5); // 0-2.5 points

    // Macro contribution (Unemployment)
    let unemploymentScore = 0;
    if (macroImpacts && macroImpacts.length > 0) {
        const lastMacro = macroImpacts[macroImpacts.length - 1];
        // If unemployment decreases (negative impact), add points
        if (lastMacro.unemploymentImpact < 0) {
            unemploymentScore = Math.min(2, -lastMacro.unemploymentImpact * 5); // Max 2 points bonus
        } else {
            unemploymentScore = Math.max(-2, -lastMacro.unemploymentImpact * 10); // Penalty for unemployment rise
        }
    }

    const total = Math.max(0, Math.min(10, debtScore + capacityScore + primaryCareScore + unemploymentScore));

    // DEBUG: Log workforce score components
    console.log('[WorkforceScore] Components:', {
        debtScore,
        capacityScore,
        primaryCareScore,
        unemploymentScore,
        total,
        inputs: {
            tuitionSupport,
            serviceObligation,
            debtReduction,
            primaryCareShare
        }
    });

    return total;
}

/**
 * Calculate overall success gauge (0-10)
 * Weighted composite of 6 dimensions
 */
export function calculateSuccessGauge(results) {
    const { yearlyResults, householdBurden, workforceImpact, config, macroImpacts } = results;

    if (!yearlyResults || yearlyResults.length === 0) {
        return null;
    }

    const scores = {
        healthSpending: calculateHealthSpendingScore(yearlyResults, macroImpacts) || 0,
        federalDebt: calculateFederalDebtScore(yearlyResults, macroImpacts) || 0,
        householdBurden: calculateHouseholdBurdenScore(householdBurden) || 0,
        coverage: calculateCoverageScore(yearlyResults, config) || 0,
        providerStability: calculateProviderStabilityScore(config) || 0,
        workforce: calculateWorkforceScore(yearlyResults, workforceImpact, config, macroImpacts) || 0
    };

    // Ensure all scores are finite numbers
    Object.keys(scores).forEach(key => {
        if (!Number.isFinite(scores[key])) scores[key] = 0;
    });

    // Weighted composite
    // FIX: Reweighted based on user feedback (Less debt weight, more health/household)
    const overall =
        scores.healthSpending * 0.35 +
        scores.federalDebt * 0.15 +
        scores.householdBurden * 0.20 +
        scores.coverage * 0.15 +
        scores.providerStability * 0.10 +
        scores.workforce * 0.05;

    return {
        overall: Math.round(overall * 10) / 10, // Round to 1 decimal
        scores,
        rating: getRating(overall)
    };
}

/**
 * Get text rating based on score
 */
function getRating(score) {
    if (score >= 9) return 'Excellent';
    if (score >= 7) return 'Good';
    if (score >= 5) return 'Fair';
    if (score >= 3) return 'Poor';
    return 'Transition Deficit';
}

/**
 * Get color for score
 */
/** Good = green, bad = red, caution = amber */
export function getScoreColor(score) {
    if (score >= 7) return '#16a34a'; /* green — good */
    if (score >= 5) return '#d97706'; /* amber — caution */
    if (score >= 3) return '#ea580c'; /* orange */
    return '#dc2626'; /* red — bad */
}
